import { motion } from "framer-motion";
import NextArrow from "@/components/landing/NextArrow";

/**
 * Semblanza del abogado.
 *
 * Los datos duros de su carrera —escuela, cargo judicial, años, cédula— no los
 * tenemos: no existe nada público suyo. Van marcados con <Pendiente> a la
 * vista, en vez de inventados, porque el perfil de un abogado es una
 * declaración pública y presumir un cargo que no fue es un problema serio.
 */

const Pendiente = ({ children = "Por confirmar" }) => (
  <span className="inline-block whitespace-nowrap rounded-sm border border-dashed border-[var(--gm-accent)] bg-[var(--gm-accent-10)] px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--gm-accent)]">
    {children}
  </span>
);

const hitos = [
  {
    cuando: <Pendiente>Año</Pendiente>,
    titulo: "Licenciatura en Derecho",
    detalle: <>Institución, generación y titulación — <Pendiente /></>,
  },
  {
    cuando: <Pendiente>Periodo</Pendiente>,
    titulo: "Poder Judicial del Estado de Puebla",
    detalle: <>Cargo o cargos desempeñados, juzgado y materia — <Pendiente /></>,
  },
  {
    cuando: <><Pendiente>Año</Pendiente> <span className="font-serif-display italic">a la fecha</span></>,
    titulo: "Ejercicio privado en Puebla",
    detalle: <>Más de veinte años de litigio propio. Asuntos representativos — <Pendiente /></>,
  },
  {
    cuando: <Pendiente>Año</Pendiente>,
    titulo: "Especialización o posgrado",
    detalle: <>Maestría, diplomados o certificaciones — <Pendiente /></>,
  },
];

const credenciales = [
  { titulo: "Cédula profesional", valor: <Pendiente>Número por confirmar</Pendiente> },
  { titulo: "Colegios y asociaciones", valor: <Pendiente /> },
  { titulo: "Idiomas", valor: <>Español · <Pendiente>Inglés, por confirmar</Pendiente></> },
];

const aparece = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

const Semblanza = () => (
  <section id="semblanza" className="screen py-12 md:py-14 bg-[var(--gm-paper)]" data-testid="semblanza-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <motion.div {...aparece}>
        <p className="text-xs tracking-[0.3em] uppercase font-semibold text-[var(--gm-accent)] mb-4">Semblanza</p>
        <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight">
          Quién lleva <span className="italic text-[var(--gm-accent)]">su asunto</span>
        </h2>
      </motion.div>

      <div className="mt-8 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        <motion.div {...aparece} className="lg:col-span-4">
          <div
            className="aspect-[4/5] max-h-[34vh] grid place-items-center rounded-sm border border-[var(--gm-line-soft)] bg-[var(--gm-surface-60)] text-center"
            data-testid="semblanza-retrato"
          >
            <p className="text-[10px] tracking-[0.25em] uppercase leading-loose text-[var(--gm-faint)]">
              Retrato
              <br />
              pendiente
            </p>
          </div>
          <div className="mt-6">
            <p className="font-serif-display text-xl font-semibold tracking-tight">
              Guillermo Morales Rodríguez
            </p>
            <p className="mt-1 text-xs tracking-[0.18em] uppercase text-[var(--gm-faint)]">
              Abogado · Socio fundador
            </p>
          </div>
          <div className="mt-6 grid gap-3">
            {credenciales.map((c) => (
              <div key={c.titulo} className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--gm-accent)]">
                  {c.titulo}
                </p>
                <p className="text-sm font-light leading-relaxed text-[var(--gm-muted)]">{c.valor}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          {...aparece}
          transition={{ ...aparece.transition, delay: 0.15 }}
          className="lg:col-span-8"
        >
          <p className="max-w-2xl text-base font-light leading-relaxed text-[var(--gm-muted)]">
            Litigante con más de veinte años de ejercicio en Puebla y una trayectoria previa dentro
            del Poder Judicial del Estado. Esa doble mirada —la del juzgador y la del abogado de
            parte— es la que ordena cada estrategia: sabemos cómo se lee un expediente desde el otro
            lado del escritorio.
          </p>
          <p className="mt-3 max-w-2xl text-base font-light leading-relaxed text-[var(--gm-muted)]">
            Atiende personalmente los asuntos de la firma en materia civil, mercantil, familiar,
            laboral e inmobiliaria, y encabeza la consultoría preventiva para empresas.
          </p>

          {/* Los años son una secuencia real: por eso la línea de tiempo va por fecha. */}
          <div className="mt-6 border-t border-[var(--gm-line-soft)]">
            {hitos.map((h) => (
              <div
                key={h.titulo}
                className="grid gap-2 border-b border-[var(--gm-line-soft)] py-3 sm:grid-cols-[11rem_minmax(0,1fr)] sm:gap-6"
              >
                <div className="text-sm text-[var(--gm-accent)]">{h.cuando}</div>
                <div>
                  <h3 className="text-[15px] font-medium tracking-tight">{h.titulo}</h3>
                  <p className="mt-1.5 max-w-xl text-sm font-light leading-relaxed text-[var(--gm-muted)]">
                    {h.detalle}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
        <NextArrow to="plataforma" />
</section>
);

export default Semblanza;
